import React, { useState, useEffect } from "react";
import { supabase } from "../utils/supabase";
import { MemeSubmission } from "../types";

const PublicGallery = () => {
  const [memes, setMemes] = useState<MemeSubmission[]>([]);
  const [selectedMeme, setSelectedMeme] = useState<MemeSubmission | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPublicMemes();
  }, []);

  const shuffleArray = (array: MemeSubmission[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const loadPublicMemes = async () => {
    if (!supabase) return;

    try {
      const { data: memesData, error: memesError } = await supabase
        .from("public_memes")
        .select("*")
        .order("created_at", { ascending: false });

      if (memesError) throw memesError;

      // Shuffle the memes array on first load
      const shuffledMemes = shuffleArray(memesData || []);
      setMemes(shuffledMemes);

      // Set the first meme as selected by default
      if (shuffledMemes.length > 0) {
        setSelectedMeme(shuffledMemes[0]);
      }
    } catch (error) {
      console.error("Error loading public memes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleThumbnailClick = (meme: MemeSubmission) => {
    setSelectedMeme(meme);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-700 text-lg" dir="rtl">
            טוען גלריית המימז...
          </p>
        </div>
      </div>
    );
  }

  if (memes.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎭</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2" dir="rtl">
            עדיין אין מימז ציבוריים
          </h2>
          <p className="text-gray-600" dir="rtl">
            המימז הראשונים יופיעו כאן בקרוב
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Main Display Area - Using same dimensions as MemeCanvas */}
      <div className="w-[90%] mx-auto bg-white rounded-lg m-4 shadow-lg border border-black-100 py-2">
        <div className="flex justify-center">
          <div className="w-full max-w-4xl">
            <div className="relative" style={{ aspectRatio: "4/3" }}>
              {selectedMeme ? (
                <img
                  src={selectedMeme.image_url}
                  alt="Selected Meme"
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      "https://via.placeholder.com/800x600?text=Meme";
                  }}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <div className="text-4xl mb-2">🎭</div>
                    <p dir="rtl">בחר מימ לצפייה</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Thumbnail Grid Container */}
      <div className="w-[90%] mx-auto bg-white rounded-xl m-4 shadow-lg border border-black-100">
        <div className="bg-white/70 backdrop-blur-sm shadow-xl border border-pink-100 overflow-hidden md:rounded-2xl">
          {/* Thumbnail Grid */}
          <div className="p-4 bg-white/50">
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3" dir="rtl">
              {memes.map((meme, index) => (
                <div
                  key={meme.id}
                  onClick={() => handleThumbnailClick(meme)}
                  className={`cursor-pointer transition-all duration-200 rounded-lg overflow-hidden border-2 ${
                    selectedMeme?.id === meme.id
                      ? "border-pink-500 shadow-lg scale-105"
                      : "border-gray-200 hover:border-pink-300 hover:shadow-md"
                  }`}
                >
                  <img
                    src={meme.image_url}
                    alt={`Meme ${index + 1}`}
                    className="w-full h-full aspect-square object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "https://via.placeholder.com/80x80?text=Meme";
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Additional Info */}
      <div className="text-center mt-4 px-4">
        <p className="text-xs sm:text-sm text-gray-600" dir="rtl">
          לחץ על המימזים הקטנים למטה כדי לראות אותם בגודל מלא
        </p>
      </div>
    </div>
  );
};

export default PublicGallery;
